import { Subject, Bookable, Teacher, Grade, Time, Course, Lesson, Holiday, TimetableMeta } from 'src/store/models/timetable/index.js'
import { LessonGrade, LessonBookable, LessonTeacher } from 'src/store/models/pivot/index.js'
import Timetable from 'src/api/timetable.js'
import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'
import { i18n } from 'src/config/i18n.ts'

// TODO stop importing the mixin!
import schoolYearMixin from 'src/mixins/management/schoolYearMixin.js'

const managementEntities = [Subject, Bookable, Teacher, Grade, Time, Course, Lesson, Holiday, TimetableMeta]

const defaultState = () => ({
  headers: {
    school: null,
    'version-uuid': null
  },
  projects: [],
  timetableFromPlanner: null,
  entityLoadGeneration: 0,
})

export const state = defaultState()

export const mutations = {
  RESET_STATE (state) {
    Object.assign(state, defaultState())
  },
  SET_HEADER (state, timetableMeta) {
    state.headers = {
      school: timetableMeta.school_id,
      'version-uuid': timetableMeta.id
    }
  },
  SET_TIMETABLE_FROM_PLANNER (state, value) {
    state.timetableFromPlanner = value
  },
  TT_PLANNER_PROJECTS (state, projects) {
    state.projects = projects
  },
  INCREMENT_ENTITY_LOAD_GENERATION (state) {
    state.entityLoadGeneration++
  }
}

// Route entity (plural) → query param (singular)
const ENTITY_TO_PARAM = {
  grades: 'grade',
  teachers: 'teacher',
  bookables: 'bookable'
}

// Build Phase-2 filter params for lessons or courses.
// Courses have no bookable pivot, so the bookables filter is skipped for the 'courses' type.
function buildEntityParams (entity, entityId, type) {
  if (type === 'courses' && entity === 'bookables') return {}
  const param = ENTITY_TO_PARAM[entity]
  if (!param || !entityId) return {}
  return { [param]: entityId }
}

export const actions = {
  async getTimetablePlannerVersion ({ commit }, { school_id: schoolId }) {
    // check if there is a version on the server
    const { data } = await Timetable.getTimetableEditorVersion({
      headers: {
        school: schoolId
      }
    })
    commit('TT_PLANNER_PROJECTS', data)

    return data
  },

  async createTimetableProject ({ commit, dispatch }, { timetableMeta, copyVersion }) {
    await Timetable.postTimetableEditorVersion({
      id: timetableMeta.id,
      name: timetableMeta.getProjectName(),
      copy_data_from_version: copyVersion
    })

    commit('SET_HEADER', timetableMeta)

    await timetableMeta.$createAndPush({ waitForServer: true })

    await dispatch('emptyTimeTableEntities')
    await dispatch('loadManagementTimetableEntities', { school_id: timetableMeta.school_id })
    await dispatch('getTimetablePlannerVersion', { school_id: timetableMeta.school_id })
  },

  async initManagementTimetableProject ({ dispatch }, { school_id: schoolId, entity, entity_id: entityId, skipHeavyEntities = false }) {
    const projects = await dispatch('getTimetablePlannerVersion', { school_id: schoolId })

    if (!projects.length) {
      const timetableMeta = new TimetableMeta({ id: GlobalPrototypes.uuid(), school_id: schoolId })

      await dispatch('createTimetableProject', { timetableMeta })
      return
    }

    // select suggest school year or newest
    const suggestedSchoolYear = schoolYearMixin.computed.suggestSchoolYearRangePrototype()
    const currentProject = projects.find(p => p.name === suggestedSchoolYear.name) || [...projects].sort((a, b) => a.name < b.name ? 1 : -1)[0]

    await dispatch('loadManagementTimetableProject', {
      id: currentProject?.id,
      school_id: schoolId,
      entity,
      entity_id: entityId,
      skipHeavyEntities
    })
  },

  async loadManagementTimetableProject ({ dispatch, commit, state }, { id, school_id: schoolId, entity, entity_id: entityId, skipHeavyEntities = false }) {
    await dispatch('emptyTimeTableEntities')

    commit('SET_HEADER', {
      id,
      school_id: schoolId
    })

    // Restore a minimal TimetableMeta from the project list so UI elements
    // (e.g. the project-switch dropdown) remain visible while base data loads.
    const project = state.projects.find(p => p.id === id)
    if (project) {
      TimetableMeta.insertOrUpdate({ data: { ...project, school_id: schoolId } })
    }

    await dispatch('loadManagementTimetableEntities', { entity, entity_id: entityId, skipHeavyEntities })
  },

  async loadManagementTimetableEntities ({ dispatch, commit, state }, { school_id: schoolId, entity, entity_id: entityId, reload = false, skipHeavyEntities = false } = {}) {
    if (!state.headers.school && !schoolId) return
    if (!state.headers.school) return dispatch('initManagementTimetableProject', { school_id: schoolId, entity, entity_id: entityId, skipHeavyEntities })

    // Lessons are filtered by entity (grade/teacher/bookable). Without a valid filter the request
    // would fetch ALL lessons, which is unnecessary on pages like basedata that don't display them.
    const lessonParams = buildEntityParams(entity, entityId, 'lessons')
    const shouldLoadLessons = Object.keys(lessonParams).length > 0

    const heavyEntities = managementEntities.filter(s => s.entity === 'courses' || (s.entity === 'lessons' && shouldLoadLessons))

    // When not reloading, first fetch base entities (everything except lessons/courses)
    // so the UI can render grades, teachers, subjects, times etc. before the heavy data arrives.
    if (!reload) {
      const baseEntities = managementEntities.filter(s => s.entity !== 'courses' && s.entity !== 'lessons')
      const baseRes = await Timetable.getTimetableEditorCurrent({
        headers: state.headers,
        params: { without_lessons: true, without_courses: true }
      })
      baseEntities.forEach(s => s.insertOrUpdate({ data: baseRes.data[s.entity] }))
      // On the management-timetable page the selectedEntity watcher fires after navigation and
      // loads courses/lessons filtered by the correct new entity. Skip the heavy load here so
      // we don't fire an extra request with the stale entity filter from the previous route.
      if (skipHeavyEntities) return
    }

    // Increment the entity generation counter so that concurrent entity switches
    // (e.g. auto-select first grade followed by user selecting another grade) discard
    // stale responses instead of overwriting the store with the wrong entity's data.
    commit('INCREMENT_ENTITY_LOAD_GENERATION')
    const capturedGeneration = state.entityLoadGeneration

    // Load courses (and lessons when an entity filter is available) in parallel.
    // Capture the current version so we can discard results if the project
    // switches while the requests are still in-flight.
    const capturedVersion = state.headers['version-uuid']
    try {
      const requests = [
        Timetable.getTimetableEditorCourses({ headers: state.headers, params: buildEntityParams(entity, entityId, 'courses') }),
        ...(shouldLoadLessons
          ? [Timetable.getTimetableEditorLessons({ headers: state.headers, params: lessonParams })]
          : [])
      ]

      const [coursesRes, lessonsRes] = await Promise.all(requests)

      if (!coursesRes.data || (shouldLoadLessons && !lessonsRes?.data)) throw new Error('No data returned from server')
      if (state.headers['version-uuid'] !== capturedVersion) return
      // Discard stale entity responses: if another entity switch happened while
      // this request was in-flight, the generation will have advanced.
      if (state.entityLoadGeneration !== capturedGeneration) return

      const heavyData = { ...coursesRes.data, ...(lessonsRes?.data ?? {}) }
      // On reload, use create() to replace all existing records with the fresh filtered set.
      // On initial load, use insertOrUpdate() to merge into the store.
      const persistMethod = reload ? 'create' : 'insertOrUpdate'
      heavyEntities.forEach(s => s[persistMethod]({ data: heavyData[s.entity] }))
    } catch {
      GlobalPrototypes.toastNotify({
        type: 'error',
        text: i18n.global.t('management.management_table.error_notification_reload_table')
      })
    }
  },

  async emptyTimeTableEntities () {
    const promises = managementEntities.map(s => s.deleteAll())
    await Promise.all(promises)
    await TimetableMeta.deleteAll()
    await LessonGrade.deleteAll()
    await LessonTeacher.deleteAll()
    await LessonBookable.deleteAll()
  },

  async switchTimeTableEntities ({ dispatch }, { toManagement, school_id: schoolId, entity, entity_id: entityId, skipEntityReload = false }) {
    const sameSchool = !schoolId || state.headers.school === schoolId
    const sameMode = state.timetableFromPlanner === toManagement

    if (sameMode && sameSchool) {
      // Same project is loaded. Always reload heavy entities so pages like basedata/course
      // get correctly filtered (or unfiltered) courses after coming from timetable/grades/1a.
      // The management-timetable page manages entity reloads via its selectedEntity watcher, so
      // skip here to avoid a duplicate request.
      if (!skipEntityReload) {
        return dispatch('loadManagementTimetableEntities', { entity, entity_id: entityId, reload: true })
      }
      return
    }

    await dispatch('emptyTimeTableEntities')
    GlobalPrototypes.store.commit('timetablePlanner/SET_TIMETABLE_FROM_PLANNER', toManagement)
    if (!toManagement) return
    // The entity filter is passed through to loadManagementTimetableEntities so the
    // init loads filtered heavy entities directly. The entityLoadGeneration guard
    // discards any stale reload the selectedEntity watcher may have fired on mount.
    return dispatch('initManagementTimetableProject', {
      school_id: schoolId || state.headers.school,
      entity,
      entity_id: entityId,
      skipHeavyEntities: false
    })
  }
}
